/**
 * Matter Report - API Service
 *
 * HTTP client for Matter Ledger and Statement report API endpoints
 */

import { api } from '@xtnd/web/shared/api';

// Request parameter types
interface MatterLedgerParams {
  matterId: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

interface MatterStatementParams {
  matterId: string;
  startDate: string;
  endDate: string;
}

interface MatterExportParams {
  matterId: string;
  type: 'csv' | 'xls' | 'pdf';
  startDate?: string;
  endDate?: string;
  includeMetadata?: boolean;
}

class MatterReportApiService {
  /**
   * Build query string from parameters
   */
  private buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(`${key}[]`, String(item)));
        } else {
          searchParams.set(key, String(value));
        }
      }
    });

    return searchParams.toString();
  }

  /**
   * Get matter ledger transactions
   */
  async getLedger(params: MatterLedgerParams) {
    const { matterId, ...queryParams } = params;
    const queryString = this.buildQueryString(queryParams as Record<string, unknown>);
    const endpoint = `/reports/matter/${matterId}/ledger${queryString ? `?${queryString}` : ''}`;

    return await api.get(endpoint);
  }

  /**
   * Get matter statement data
   */
  async getStatement(params: MatterStatementParams) {
    const { matterId, ...queryParams } = params;
    const queryString = this.buildQueryString(queryParams as Record<string, unknown>);
    const endpoint = `/reports/matter/${matterId}/statement${queryString ? `?${queryString}` : ''}`;

    return await api.get(endpoint);
  }

  /**
   * Export matter ledger report
   */
  async exportLedger(params: MatterExportParams): Promise<void> {
    const { matterId, ...exportParams } = params;
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/reports/matter/${matterId}/ledger/export`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(exportParams),
      }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    // Get filename from response headers or use default with timestamp
    const contentDisposition = response.headers.get('content-disposition');
    const now = new Date();
    const timeStamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    let filename = `matter-ledger-${matterId}-${timeStamp}.${params.type}`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Create temporary download link
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get list of matters for selection from DynamoDB
   * Fetches all matters for the tenant (not just open/pending)
   */
  async getMatters(search?: string): Promise<Array<{ id: string; description: string; number?: string; displayNumber?: string }>> {
    const queryParams = new URLSearchParams();
    if (search && search.trim()) {
      queryParams.set('search', search.trim());
    }

    const endpoint = `/clio/matters${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<{ matters: Array<{ id: string; description: string; number?: string; displayNumber?: string }> }>(endpoint);
    return response.matters || [];
  }

  /**
   * Export matter statement report
   */
  async exportStatement(params: MatterExportParams): Promise<void> {
    const { matterId, ...exportParams } = params;
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/reports/matter/${matterId}/statement/export`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(exportParams),
      }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    // Get filename from response headers or use default with timestamp
    const contentDisposition = response.headers.get('content-disposition');
    const now = new Date();
    const timeStamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    let filename = `matter-statement-${matterId}-${timeStamp}.${params.type}`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Create temporary download link
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

// Export singleton instance
export const matterReportApi = new MatterReportApiService();

// Export types for use in components
export type { MatterLedgerParams, MatterStatementParams, MatterExportParams };

